'use client'

import { useCallback, useMemo } from 'react'

import styles from './Tabs.module.scss'

export interface TabItem<T = string> {
  id: string
  label: string
  icon?: React.ReactNode
  value: T
  panelId: string
}

interface TabsProps<T = string> {
  tabs: TabItem<T>[]
  selected: T
  onTabChange: (value: T) => void
  ariaLabel: string
}

export const Tabs = <T,>({
  tabs,
  selected,
  onTabChange,
  ariaLabel
}: TabsProps<T>) => {
  const selectedIndex = useMemo(
    () => tabs.findIndex(tab => tab.value === selected),
    [tabs, selected]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const count = tabs.length
      const next = () => onTabChange(tabs[(selectedIndex + 1) % count].value)
      const prev = () =>
        onTabChange(tabs[(selectedIndex - 1 + count) % count].value)
      const first = () => onTabChange(tabs[0].value)
      const last = () => onTabChange(tabs[count - 1].value)

      const keyMap: Record<string, () => void> = {
        ArrowRight: next,
        ArrowLeft: prev,
        Home: first,
        End: last
      }

      const action = keyMap[event.key]

      if (action) {
        event.preventDefault()
        action()
      }
    },
    [onTabChange, selectedIndex, tabs]
  )

  return (
    <div
      className={styles.tabList}
      role='tablist'
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      {tabs.map(tab => (
        <button
          className={styles.tabButton}
          key={tab.id}
          id={tab.id}
          role='tab'
          aria-controls={tab.panelId}
          aria-selected={selected === tab.value}
          onClick={() => onTabChange(tab.value)}
          onKeyDown={handleKeyDown}
          tabIndex={selected === tab.value ? 0 : -1}
        >
          {tab.icon && <span>{tab.icon}</span>}
          {tab.label}
        </button>
      ))}
    </div>
  )
}

export default Tabs
